import type { JupiterQuote } from "@/types";
import { DEFAULT_SLIPPAGE_BPS, SOL_ADDRESS, SOL_MINT } from "./utils";

const JUPITER_QUOTE_API = "https://lite-api.jup.ag/swap/v1";
const JUPITER_ORDER_API = "https://api.jup.ag/swap/v2/order";

export interface JupiterOrderResponse {
	transaction: string; // base64-encoded pre-built transaction
	inAmount: string;
	outAmount: string;
	otherAmountThreshold: string;
	slippageBps: number;
	priceImpactPct: string;
	routePlan: JupiterQuote["routePlan"];
	lastValidBlockHeight: string;
	requestId: string;
}

export function toJupiterMint(address: string): string {
	return address === SOL_ADDRESS ? SOL_MINT : address;
}

export async function fetchJupiterQuote(
	inputMint: string,
	outputMint: string,
	amount: bigint,
	slippageBps = DEFAULT_SLIPPAGE_BPS,
): Promise<JupiterQuote> {
	const params = new URLSearchParams({
		inputMint: toJupiterMint(inputMint),
		outputMint: toJupiterMint(outputMint),
		amount: amount.toString(),
		slippageBps: slippageBps.toString(),
	});

	const res = await fetch(`${JUPITER_QUOTE_API}/quote?${params.toString()}`);
	if (!res.ok) {
		const text = await res.text().catch(() => "");
		throw new Error(`Jupiter quote error ${res.status}: ${text}`);
	}
	return res.json() as Promise<JupiterQuote>;
}

export async function fetchJupiterOrder(
	inputMint: string,
	outputMint: string,
	amount: bigint,
	taker: string,
	payer: string,
	slippageBps = DEFAULT_SLIPPAGE_BPS,
): Promise<JupiterOrderResponse> {
	const params = new URLSearchParams({
		inputMint: toJupiterMint(inputMint),
		outputMint: toJupiterMint(outputMint),
		amount: amount.toString(),
		taker,
		payer,
		slippageBps: slippageBps.toString(),
	});

	const res = await fetch(`${JUPITER_ORDER_API}?${params}`);
	if (!res.ok) {
		const text = await res.text().catch(() => "");
		throw new Error(`Jupiter order error ${res.status}: ${text}`);
	}
	return res.json() as Promise<JupiterOrderResponse>;
}

export function formatPriceImpact(pct: string): string {
	const n = parseFloat(pct);
	if (n < 0.01) return "<0.01%";
	return `${n.toFixed(2)}%`;
}

export function calcMinReceived(outAmount: string, slippageBps: number, decimals: number): number {
	const raw = BigInt(outAmount);
	const min = (raw * BigInt(10_000 - slippageBps)) / BigInt(10_000);
	return Number(min) / 10 ** decimals;
}
